import { Helmet } from '@modern-js/runtime/head';
import { useLoaderData } from '@modern-js/runtime/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';

import type { SitePage } from '../common/interface';
import { AuthFailed, CodeblockRenderer, NotFound } from '../components';
import { useLayout } from '../contexts/LayoutContext';
import {
  generateBrandingLogoCssVariables,
  generateGoogleFontsUrl,
  generateSemanticCssFallback,
  generateSiteThemeCssVariables,
  generateSiteThemeFontUrl,
} from '../utils/siteTheme';
import { getDefaultPublicFaviconUrl } from '../config/apiConfig';
import { LAYOUT_SCRIPTS } from './layout-scripts-constants';
// import Hero from '../components/Hero';
import type { LoaderData } from './page.data';

// Track which layout scripts we've already executed/loaded on the client
// to avoid double-running them on re-renders or client-side navigations.
const executedLayoutInlineScripts = new Set<string>();
const loadedLayoutExternalScripts = new Set<string>();

/**
 * Root page component that handles both cases:
 * SSR homepage: load portal via token and render homepage
 */
const Index = () => {
  // Get SSR loader data - this is hydration-safe (can be null during hydration)
  const loaderData = useLoaderData() as LoaderData | null;
  const [hasHydrated, setHasHydrated] = useState(false);
  const { setShouldHideLayout, setHeaderVisible, setLayoutScriptsReady } =
    useLayout();

  useEffect(() => setHasHydrated(true), []);

  // Find matching homepage - works both server-side and client-side
  const getMatchingHomepage = () => {
    if (!loaderData?.siteData) return null;

    // Normalize a path to leading-slash, no trailing-slash (except root)
    const normalize = (p?: string | null) => {
      const v = (p || '').trim();
      if (v === '' || v === '/') return '/';
      const withSlash = v.startsWith('/') ? v : `/${v}`;
      return withSlash !== '/' ? withSlash.replace(/\/+$/, '') : '/';
    };

    // Look for homepage - either normalized permalink === '/' or homePage === true
    return (
      loaderData.siteData.data.find(
        page => normalize(page.permalink) === '/' || page.homePage === true,
      ) || null
    );
  };

  // Determine the current state for rendering
  const getCurrentState = () => {
    // Check for SSR loader errors first (available immediately)
    if (loaderData?.error) {
      if (loaderData.error.type === 'validation') {
        return 'invalid-siteid';
      }
      if (loaderData.error.type === 'auth') {
        return 'auth-error';
      }
      return 'invalid-siteid';
    }

    // If no loader data and not hydrated yet, show hydrating state
    if (!loaderData && !hasHydrated) {
      return 'hydrating';
    }

    // If we have site data and can find homepage (works in SSR)
    const matchingHomepage = getMatchingHomepage();
    if (loaderData?.siteData && matchingHomepage) {
      return 'homepage';
    }

    // If we have site data but no homepage found
    if (loaderData?.siteData && !matchingHomepage) {
      return 'no-homepage';
    }

    // No site data
    return 'no-site-found';
  };

  const currentState = getCurrentState();

  // Redirect to /session-expired when auth error is detected
  useEffect(() => {
    if (currentState === 'auth-error' && hasHydrated) {
      // Prevent redirect loop - only redirect if not already navigating to session-expired
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/session-expired')) {
        // Use window.location for full page navigation to exit SSR app context
        window.location.replace('/session-expired');
      }
    }
  }, [currentState, hasHydrated]);

  // Hide layout only when showing error states (not homepage or hydrating),
  // and propagate header visibility from loader data into layout context.
  useEffect(() => {
    const isErrorState =
      currentState !== 'homepage' && currentState !== 'hydrating';
    setShouldHideLayout(isErrorState);

    const headerVisibleFromLoader =
      loaderData && typeof loaderData === 'object'
        ? (loaderData as { headerVisible?: boolean }).headerVisible
        : undefined;
    setHeaderVisible(headerVisibleFromLoader ?? true);
  }, [currentState, loaderData, setHeaderVisible, setShouldHideLayout]);

  const layoutHtml = ((): string | null | undefined => {
    if (!loaderData || typeof loaderData !== 'object') return null;
    const direct = (loaderData as { layoutHtml?: string | null }).layoutHtml;
    if (direct && typeof direct === 'string' && direct.length > 0) {
      return direct;
    }
    const fallback = (
      loaderData as unknown as {
        siteData?: {
          layoutDetails?: { layoutData?: { htmlContent?: unknown } };
        };
      }
    )?.siteData?.layoutDetails?.layoutData?.htmlContent;
    return typeof fallback === 'string' ? fallback : null;
  })();
  const layoutActive = Boolean(
    loaderData &&
      typeof loaderData === 'object' &&
      (loaderData as { layoutActive?: boolean }).layoutActive,
  );

  // Generate site theme CSS variables from loader data
  const siteThemeCss = useMemo(() => {
    const siteTheme = (loaderData as { siteTheme?: unknown } | null)?.siteTheme;
    if (!siteTheme || typeof siteTheme !== 'object') {
      return '';
    }
    return generateSiteThemeCssVariables(
      siteTheme as Parameters<typeof generateSiteThemeCssVariables>[0],
    );
  }, [loaderData]);

  // Semantic CSS fallback for Tailwind CDN (always needed)
  const semanticCssFallback = useMemo(
    () => generateSemanticCssFallback(loaderData?.siteTheme),
    [loaderData?.siteTheme],
  );

  // Google Fonts URL for branded fonts from theming data
  const googleFontsUrl = useMemo(
    () => generateGoogleFontsUrl(loaderData?.themeVars),
    [loaderData?.themeVars],
  );

  // Custom font CSS URL from the tenant's uploaded font families
  const customFontCssUrl = loaderData?.customFontCssUrl ?? null;

  // Google Fonts URL for site theme font (if it's a Google Font)
  // Skip if customFontCssUrl is present (indicates a custom uploaded font)
  const siteThemeFontUrl = useMemo(() => {
    if (customFontCssUrl) return null; // Custom font CSS will handle this
    return generateSiteThemeFontUrl(loaderData?.siteTheme?.fontFamily);
  }, [loaderData?.siteTheme?.fontFamily, customFontCssUrl]);

  // Branding logo CSS variables from theming data
  const brandingLogoCss = useMemo(
    () => generateBrandingLogoCssVariables(loaderData?.themeVars),
    [loaderData?.themeVars],
  );

  const splitLayoutBody = (html: string | null | undefined) => {
    try {
      if (!html || typeof html !== 'string') return null;
      // Extract body content only
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const body = bodyMatch ? bodyMatch[1] : html;
      // Find sw-page-root container
      const openMatch = body.match(/(<div[^>]*id=["']sw-page-root["'][^>]*>)/i);
      if (!openMatch) return null;
      const openTag = openMatch[1];
      // Parse attributes from openTag
      const attrs: Record<string, string> = {};
      const attrRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
      let m: RegExpExecArray | null = attrRegex.exec(openTag);
      while (m !== null) {
        attrs[m[1]] = m[2];
        m = attrRegex.exec(openTag);
      }
      const rootId = attrs.id;
      const rootClass = attrs.class;
      const dataEntries = Object.entries(attrs).filter(([k]) =>
        k.startsWith('data-'),
      );
      return { root: { id: rootId, className: rootClass, data: dataEntries } };
    } catch {
      return null;
    }
  };

  const extractHeadScripts = useCallback(
    (html: string | null | undefined): string[] => {
      try {
        if (!html || typeof html !== 'string') return [];
        const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
        const head = headMatch ? headMatch[1] : '';
        const scripts: string[] = [];
        const scriptRegex =
          /<script[^>]*src=["']([^"']+)["'][^>]*><\/script>/gi;
        let sm: RegExpExecArray | null = scriptRegex.exec(head);
        while (sm !== null) {
          scripts.push(sm[1]);
          sm = scriptRegex.exec(head);
        }
        return scripts;
      } catch {
        return [];
      }
    },
    [],
  );

  // Shared helper: parse all attributes from a <link> tag's attribute string,
  // including boolean attributes like `crossorigin` that have no value.
  const parseLinkAttrs = (attrsStr: string): Record<string, string> => {
    const attrs: Record<string, string> = {};
    const kvRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
    let a = kvRegex.exec(attrsStr);
    while (a) {
      attrs[a[1]] = a[2];
      a = kvRegex.exec(attrsStr);
    }
    // Capture boolean attributes (no value) such as `crossorigin`
    const boolRegex = /(?:^|\s)(crossorigin|async|defer|nomodule)(?=\s|$|\/)/g;
    let b = boolRegex.exec(attrsStr);
    while (b) {
      attrs[b[1]] = '';
      b = boolRegex.exec(attrsStr);
    }
    return attrs;
  };

  const extractHeadStylesheets = (
    html: string | null | undefined,
  ): Array<Record<string, string>> => {
    try {
      if (!html || typeof html !== 'string') return [];
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const stylesheets: Array<Record<string, string>> = [];
      const linkRegex = /<link([^>]*?)\/?>/gi;
      let lm: RegExpExecArray | null = linkRegex.exec(head);
      while (lm !== null) {
        const attrs = parseLinkAttrs(lm[1] || '');
        if ((attrs.rel || '').toLowerCase() === 'stylesheet') {
          stylesheets.push(attrs);
        }
        lm = linkRegex.exec(head);
      }
      return stylesheets;
    } catch {
      return [];
    }
  };

  const extractHeadInlineStyles = (
    html: string | null | undefined,
  ): string[] => {
    try {
      if (!html || typeof html !== 'string') return [];
      const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const head = headMatch ? headMatch[1] : '';
      const styles: string[] = [];
      const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
      let stm: RegExpExecArray | null = styleRegex.exec(head);
      while (stm !== null) {
        styles.push(stm[1]);
        stm = styleRegex.exec(head);
      }
      return styles;
    } catch {
      return [];
    }
  };

  const extractHeadInlineScripts = useCallback(
    (html: string | null | undefined): string[] => {
      try {
        if (!html || typeof html !== 'string') return [];
        const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
        const head = headMatch ? headMatch[1] : '';
        const scripts: string[] = [];
        // Match inline scripts (without src attribute) that have content
        const scriptRegex =
          /<script(?![^>]*\ssrc=)([^>]*)>([\s\S]*?)<\/script>/gi;
        let sm: RegExpExecArray | null = scriptRegex.exec(head);
        while (sm !== null) {
          const content = sm[2]?.trim();
          if (content) {
            scripts.push(content);
          }
          sm = scriptRegex.exec(head);
        }
        return scripts;
      } catch {
        return [];
      }
    },
    [],
  );

  // Extract <html> attributes like lang/dir/data-*
  const extractHtmlAttributes = (
    html: string | null | undefined,
  ): Record<string, string> => {
    try {
      if (!html || typeof html !== 'string') return {};
      const match = html.match(/<html([^>]*)>/i);
      const attrsStr = match ? match[1] : '';
      const attrs: Record<string, string> = {};
      const attrRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
      let m: RegExpExecArray | null = attrRegex.exec(attrsStr);
      while (m) {
        const key = m[1];
        const val = m[2];
        if (key === 'lang' || key === 'dir' || key.startsWith('data-')) {
          attrs[key] = val;
        }
        m = attrRegex.exec(attrsStr);
      }
      return attrs;
    } catch {
      return {};
    }
  };

  // Extract arbitrary <meta ...> tags from <head>
  const extractHeadMeta = (
    html: string | null | undefined,
  ): Array<Record<string, string>> => {
    try {
      if (!html || typeof html !== 'string') return [];
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const metas: Array<Record<string, string>> = [];
      const metaRegex = /<meta([^>]*?)\/?>/gi;
      let m = metaRegex.exec(head);
      while (m) {
        const attrsStr = m[1] || '';
        const attrs: Record<string, string> = {};
        const attrRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
        let a = attrRegex.exec(attrsStr);
        while (a) {
          attrs[a[1]] = a[2];
          a = attrRegex.exec(attrsStr);
        }
        if (Object.keys(attrs).length) metas.push(attrs);
        m = metaRegex.exec(head);
      }
      return metas;
    } catch {
      return [];
    }
  };

  // Extract non-stylesheet <link ...> tags from <head> with allowlist
  const extractHeadLinks = (
    html: string | null | undefined,
  ): Array<Record<string, string>> => {
    try {
      if (!html || typeof html !== 'string') return [];
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const links: Array<Record<string, string>> = [];
      const linkRegex = /<link([^>]*?)\/?>/gi;
      let m = linkRegex.exec(head);
      while (m) {
        const attrs = parseLinkAttrs(m[1] || '');
        const rel = (attrs.rel || '').toLowerCase();
        // Skip stylesheets here (already injected via extractHeadStylesheets)
        if (rel === 'stylesheet') {
          m = linkRegex.exec(head);
          continue;
        }
        // Block <base> as it can break URL resolution
        if (rel === 'base') {
          m = linkRegex.exec(head);
          continue;
        }
        // Disallow icons from API; allow preconnect, dns-prefetch, manifest
        if (
          rel === 'preconnect' ||
          rel === 'dns-prefetch' ||
          rel === 'manifest'
        ) {
          links.push(attrs);
        }
        m = linkRegex.exec(head);
      }
      return links;
    } catch {
      return [];
    }
  };

  // Extract <title> tag from <head>
  const extractHeadTitle = (html: string | null | undefined): string | null => {
    try {
      if (!html || typeof html !== 'string') return null;
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const titleMatch = head.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
      return titleMatch ? titleMatch[1].trim() : null;
    } catch {
      return null;
    }
  };

  // Compute API-provided head/html attributes
  const htmlAttrs = layoutActive ? extractHtmlAttributes(layoutHtml) : {};
  const apiMetasRaw = layoutActive ? extractHeadMeta(layoutHtml) : [];
  const apiLinksRaw = layoutActive ? extractHeadLinks(layoutHtml) : [];
  const apiTitle = layoutActive ? extractHeadTitle(layoutHtml) : null;

  const metaKey = (m: Record<string, string>) =>
    (m.name || m.property || m.charset || m['http-equiv'] || '').toLowerCase();
  const apiMetasUnique = Object.values(
    apiMetasRaw.reduce(
      (acc, m) => {
        const k = metaKey(m) || `${Object.keys(acc).length}`;
        acc[k] = m;
        return acc;
      },
      {} as Record<string, Record<string, string>>,
    ),
  );
  // Drop any API-provided charset meta; server/document defines utf-8
  const apiMetasNoCharset = apiMetasUnique.filter(m => !('charset' in m));
  const hasApiViewport = apiMetasUnique.some(
    m => (m.name || '').toLowerCase() === 'viewport',
  );
  const hasApiDescription = apiMetasUnique.some(
    m => (m.name || '').toLowerCase() === 'description',
  );
  // Icons are intentionally ignored from API

  const metaReactKey = (m: Record<string, string>) =>
    `${metaKey(m)}:${m.content || ''}:${m.charset || ''}:${m['http-equiv'] || ''}`;
  const linkReactKey = (l: Record<string, string>) =>
    `${(l.rel || 'link').toLowerCase()}:${l.href || ''}:${l.sizes || ''}:${l.type || ''}`;

  // Post-hydration runner for layout <script> tags (both external and inline)
  // from the layout <head>. This ensures arbitrary JS authored in the portal
  // runs only after React has completed hydration, avoiding DOM mutations
  // during hydration that cause mismatch errors and content flicker.
  const runLayoutHeadScripts = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    // If no layout or layout not active, mark scripts as ready immediately
    if (!layoutActive || !layoutHtml) {
      setLayoutScriptsReady(true);
      return;
    }

    // Mark as not ready while loading
    setLayoutScriptsReady(false);

    try {
      // 1) Ensure external <script src="..."> tags from the layout are loaded.
      const externalSrcs = extractHeadScripts(layoutHtml);
      const toLoad = externalSrcs.filter(
        src => !loadedLayoutExternalScripts.has(src),
      );

      if (toLoad.length > 0) {
        await Promise.all(
          toLoad.map(
            src =>
              new Promise<void>(resolve => {
                try {
                  const scriptEl = document.createElement('script');
                  scriptEl.type = 'text/javascript';
                  scriptEl.src = src;
                  scriptEl.async = false;
                  scriptEl.onload = () => {
                    loadedLayoutExternalScripts.add(src);
                    resolve();
                  };
                  scriptEl.onerror = () => {
                    loadedLayoutExternalScripts.add(src);
                    resolve();
                  };
                  document.head.appendChild(scriptEl);
                } catch {
                  loadedLayoutExternalScripts.add(src);
                  resolve();
                }
              }),
          ),
        );
      }

      // 2) Run inline <script> blocks from the layout head after externals.
      const inlineScripts = extractHeadInlineScripts(layoutHtml);
      for (const scriptContent of inlineScripts) {
        const code = scriptContent.trim();
        if (!code) continue;

        if (executedLayoutInlineScripts.has(code)) {
          continue;
        }
        executedLayoutInlineScripts.add(code);

        try {
          const scriptEl = document.createElement('script');
          scriptEl.type = 'text/javascript';
          scriptEl.text = code;
          document.head.appendChild(scriptEl);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[layout-inline-script] execution error', error);
        }
      }

      // All layout scripts loaded and executed successfully
      setLayoutScriptsReady(true);
    } catch (error) {
      console.error(
        '[runLayoutHeadScripts] Error loading layout scripts:',
        error,
      );
      // Even on error, mark as ready to not block the page forever
      setLayoutScriptsReady(true);
    }
  }, [
    layoutActive,
    layoutHtml,
    extractHeadScripts,
    extractHeadInlineScripts,
    setLayoutScriptsReady,
  ]);

  useEffect(() => {
    // Fire and forget; errors are logged inside the helper.
    void runLayoutHeadScripts();
  }, [runLayoutHeadScripts]);

  return (
    <div className="app-container">
      <Helmet htmlAttributes={htmlAttrs}>
        {(layoutActive ? apiMetasNoCharset : []).map(m => (
          <meta key={metaReactKey(m)} {...m} />
        ))}
        {!hasApiViewport && (
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        )}
        <title>
          {(() => {
            const matchingHomepage = getMatchingHomepage();
            // Combine page name with layout title if both available
            if (apiTitle?.trim() && matchingHomepage?.name) {
              return `${matchingHomepage.name} - ${apiTitle}`;
            }
            // Use layout title if available
            if (apiTitle?.trim()) {
              return apiTitle;
            }
            // Fall back to page-based title logic
            if (currentState === 'homepage' && matchingHomepage?.name) {
              return `${matchingHomepage.name}`;
            }
            if (currentState === 'no-homepage') {
              return 'Homepage Not Found';
            }
            if (currentState === 'no-site-found') {
              return 'No Site Found';
            }
            return '';
          })()}
        </title>
        {!hasApiDescription && (
          <meta
            name="description"
            content="Portal - Dynamic site content management"
          />
        )}
        {(() => {
          const favicon = (loaderData as { lightFaviconUrl?: string | null })
            ?.lightFaviconUrl;
          const version = (loaderData as { tenantIdForFavicon?: string | null })
            ?.tenantIdForFavicon;
          const href =
            typeof favicon === 'string' && favicon.length > 0
              ? `${favicon}${version ? `?v=${encodeURIComponent(version)}` : ''}`
              : getDefaultPublicFaviconUrl();
          return [
            <link key="icon" rel="icon" href={href} />,
            <link key="shortcut-icon" rel="shortcut icon" href={href} />,
          ];
        })()}
        {(layoutActive ? apiLinksRaw : []).map(l => (
          // Render non-stylesheet allowed links (preconnect, dns-prefetch, manifest)
          <link key={linkReactKey(l)} {...l} />
        ))}
        {/* Google Fonts for branded fonts */}
        {googleFontsUrl && [
          <link
            key="preconnect-google"
            rel="preconnect"
            href="https://fonts.googleapis.com"
          />,
          <link
            key="preconnect-gstatic"
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="anonymous"
          />,
          <link key="google-fonts" href={googleFontsUrl} rel="stylesheet" />,
        ]}
        {/* Custom font CSS from tenant-uploaded font families */}
        {customFontCssUrl && (
          <link
            key="custom-font-css"
            href={customFontCssUrl}
            rel="stylesheet"
          />
        )}
        {/* Google Fonts for site theme font (if different from branded and is a Google Font) */}
        {siteThemeFontUrl && siteThemeFontUrl !== googleFontsUrl && (
          <link
            key="google-fonts-site-theme"
            href={siteThemeFontUrl}
            rel="stylesheet"
          />
        )}
        {/* Site theme CSS variables for consistent branding - MUST come before Tailwind */}
        {siteThemeCss && <style data-site-theme="true">{siteThemeCss}</style>}
        {/* Branding logo CSS variables */}
        {brandingLogoCss && (
          <style data-branding-logos="true">{brandingLogoCss}</style>
        )}
        {/* Configure Tailwind to use our CSS variables */}
        <script>{`
          tailwind.config = {
            theme: {
              extend: {
                colors: {
                  primary: 'var(--primary, var(--site-primary, #3b82f6))',
                  'primary-foreground': 'var(--primary-foreground, #ffffff)',
                  secondary: 'var(--secondary, var(--site-secondary, #64748b))',
                  background: 'var(--background, var(--site-bg, #ffffff))',
                  foreground: 'var(--foreground, var(--site-text, #1f2937))',
                  muted: 'var(--muted, #f1f5f9)',
                  'muted-foreground': 'var(--muted-foreground, #64748b)',
                  accent: 'var(--accent, #f1f5f9)',
                  'accent-foreground': 'var(--accent-foreground, #1f2937)',
                  destructive: 'var(--destructive, #ef4444)',
                  'destructive-foreground': 'var(--destructive-foreground, #ffffff)',
                  border: 'var(--border, #e2e8f0)',
                  ring: 'var(--ring, var(--site-primary, #3b82f6))',
                },
                fontFamily: {
                  sans: 'var(--font-sans, var(--site-font, ui-sans-serif, system-ui, sans-serif))',
                },
              },
            },
          }
        `}</script>
        {/* Semantic CSS fallback for non-Tailwind contexts */}
        <style data-semantic-fallback="true">{semanticCssFallback}</style>
        {/* Layout stylesheets - load before inline styles */}
        {(layoutActive ? extractHeadStylesheets(layoutHtml) : []).map(attrs => (
          <link key={linkReactKey(attrs)} {...attrs} />
        ))}
        {/* Layout inline styles */}
        {(layoutActive ? extractHeadInlineStyles(layoutHtml) : []).map(css => {
          const key = `layout-style-${css.length}-${css.charCodeAt(0) || 0}-${css.charCodeAt(css.length - 1) || 0}`;
          return <style key={key}>{css}</style>;
        })}
        {/* Include layout scripts */}
        <script>{LAYOUT_SCRIPTS.pendo}</script>
        {/* Layout external scripts - inject in SSR to ensure correct order */}
        {(layoutActive ? extractHeadScripts(layoutHtml) : []).map(src => (
          <script key={src} src={src} />
        ))}
        {/* Fallback: Inject Tailwind when no layout active */}
        {!layoutActive && <script src="https://cdn.tailwindcss.com" />}
      </Helmet>

      <main className="main-content">
        {currentState === 'homepage' &&
          (() => {
            const matchingHomepage = getMatchingHomepage();
            if (!matchingHomepage) return null;
            const parts = splitLayoutBody(layoutHtml || null);
            if (!parts) {
              return <HomePageRenderer page={matchingHomepage} />;
            }
            const rootProps: Record<string, string> = {};
            if (parts.root.id) rootProps.id = parts.root.id;
            if (layoutActive && parts.root.className)
              rootProps.className = parts.root.className;
            for (const [k, v] of parts.root.data) rootProps[k] = v;
            return (
              <>
                <div {...rootProps}>
                  <HomePageRenderer page={matchingHomepage} />
                </div>
              </>
            );
          })()}

        {/* Show error states */}
        {currentState === 'auth-error' && <AuthFailed loginHref="/" />}

        {/* removed invalid-siteid state */}

        {currentState === 'no-homepage' && <NotFound variant="no-homepage" />}

        {/* removed loading-site state */}

        {currentState === 'hydrating' && (
          <div className="site-id-instruction-container">
            <div className="site-id-instruction-content">
              <div className="instruction-icon">🔄</div>
              <h1 className="instruction-title">Loading...</h1>
              <p className="instruction-message">Initializing application...</p>
            </div>
          </div>
        )}

        {currentState === 'no-site-found' && (
          <NotFound variant="no-site-found" />
        )}
      </main>
    </div>
  );
};

/**
 * Homepage Renderer Component
 * Renders the homepage content with all its sections
 */
interface HomePageRendererProps {
  page: SitePage;
}

const HomePageRenderer: React.FC<HomePageRendererProps> = ({ page }) => {
  return (
    <div>
      {/* Homepage Content - Render all sections */}
      <div>
        {page.sections.length > 0 ? (
          <>
            {page.sections.map((section, index) => (
              // Render section directly without additional wrapper styling
              <CodeblockRenderer
                key={section.id || `section-${index}`}
                section={section}
                showDebugInfo={false}
              />
            ))}
          </>
        ) : (
          <section
            aria-label="Empty homepage"
            className="flex w-full min-h-[60vh] items-center justify-center px-4"
          >
            <div className="text-center space-y-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                Welcome to the homepage
              </h1>
              <p className="text-gray-600">
                This homepage has no content sections yet.
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Index;
